import { parse } from '@babel/parser';
import traverse, { NodePath } from '@babel/traverse';
import generate from '@babel/generator';
import * as t from '@babel/types';
import { FileOperations } from './files';
import { ASTModification } from './types';

export class ASTEngine {
  /**
   * Parse JavaScript/TypeScript file to AST
   */
  static async parseFile(filePath: string): Promise<t.File | null> {
    const content = await FileOperations.readFile(filePath);
    if (!content) {
      return null;
    }

    try {
      return parse(content, {
        sourceType: 'module',
        allowImportExportEverywhere: true,
        allowReturnOutsideFunction: true,
        plugins: [
          'jsx',
          'typescript',
          'decorators-legacy',
          'classProperties',
          'objectRestSpread',
          'functionBind',
          'exportDefaultFrom',
          'exportNamespaceFrom',
          'dynamicImport',
          'nullishCoalescingOperator',
          'optionalChaining'
        ]
      });
    } catch (error) {
      console.error(`Failed to parse ${filePath}:`, error);
      return null;
    }
  }

  /**
   * Generate code from AST
   */
  static generateCode(ast: t.File): string {
    return generate(ast, {
      compact: false,
      comments: true,
      retainLines: false
    }).code;
  }

  /**
   * Add import statement to file
   */
  static async addImport(filePath: string, source: string, specifiers: string[]): Promise<boolean> {
    const ast = await this.parseFile(filePath);
    if (!ast) {
      return false;
    }

    // Check if import already exists
    let hasImport = false;
    traverse(ast, {
      ImportDeclaration(path: NodePath<t.ImportDeclaration>) {
        if (path.node.source.value === source) {
          hasImport = true;
          // Add missing specifiers
          specifiers.forEach(spec => {
            const existingSpec = path.node.specifiers.find(s => 
              t.isImportSpecifier(s) && t.isIdentifier(s.imported) && s.imported.name === spec
            );
            if (!existingSpec) {
              path.node.specifiers.push(
                t.importSpecifier(t.identifier(spec), t.identifier(spec))
              );
            }
          });
        }
      }
    });

    // Add new import if it doesn't exist
    if (!hasImport) {
      const importSpecifiers = specifiers.map(spec => 
        t.importSpecifier(t.identifier(spec), t.identifier(spec))
      );
      
      const importDeclaration = t.importDeclaration(importSpecifiers, t.stringLiteral(source));
      ast.program.body.unshift(importDeclaration);
    }

    const newContent = this.generateCode(ast);
    return await FileOperations.writeFile(filePath, newContent);
  }

  /**
   * Add export statement to file
   */
  static async addExport(filePath: string, name: string, value?: any): Promise<boolean> {
    const ast = await this.parseFile(filePath);
    if (!ast) {
      return false;
    }

    let exportDeclaration;
    if (value) {
      // Named export with value
      exportDeclaration = t.exportNamedDeclaration(
        t.variableDeclaration('const', [
          t.variableDeclarator(t.identifier(name), value)
        ])
      );
    } else {
      // Named export without value (re-export)
      exportDeclaration = t.exportNamedDeclaration(
        null,
        [t.exportSpecifier(t.identifier(name), t.identifier(name))]
      );
    }

    ast.program.body.push(exportDeclaration);
    const newContent = this.generateCode(ast);
    return await FileOperations.writeFile(filePath, newContent);
  }

  /**
   * Add function to file
   */
  static async addFunction(filePath: string, name: string, params: string[], body: t.Statement[]): Promise<boolean> {
    const ast = await this.parseFile(filePath);
    if (!ast) {
      return false;
    }

    const functionDeclaration = t.functionDeclaration(
      t.identifier(name),
      params.map(param => t.identifier(param)),
      t.blockStatement(body)
    );

    ast.program.body.push(functionDeclaration);
    const newContent = this.generateCode(ast);
    return await FileOperations.writeFile(filePath, newContent);
  }

  /**
   * Add variable declaration to file
   */
  static async addVariable(filePath: string, name: string, value: any): Promise<boolean> {
    const ast = await this.parseFile(filePath);
    if (!ast) {
      return false;
    }

    const variableDeclaration = t.variableDeclaration('const', [
      t.variableDeclarator(t.identifier(name), value)
    ]);

    ast.program.body.push(variableDeclaration);
    const newContent = this.generateCode(ast);
    return await FileOperations.writeFile(filePath, newContent);
  }

  /**
   * Apply multiple AST modifications to a file
   */
  static async applyModifications(filePath: string, modifications: ASTModification[]): Promise<boolean> {
    const ast = await this.parseFile(filePath);
    if (!ast) {
      return false;
    }

    let modified = false;

    for (const mod of modifications) {
      switch (mod.operation) {
        case 'addImport':
          if (await this.addImport(filePath, mod.data.source, mod.data.specifiers)) {
            modified = true;
          }
          break;
        case 'addExport':
          if (await this.addExport(filePath, mod.data.name, mod.data.value)) {
            modified = true;
          }
          break;
        case 'addFunction':
          if (await this.addFunction(filePath, mod.data.name, mod.data.params, mod.data.body)) {
            modified = true;
          }
          break;
        case 'addVariable':
          if (await this.addVariable(filePath, mod.data.name, mod.data.value)) {
            modified = true;
          }
          break;
      }
    }

    return modified;
  }

  /**
   * Find and replace text in file (simple string replacement)
   */
  static async replaceText(filePath: string, searchValue: string | RegExp, replaceValue: string): Promise<boolean> {
    const content = await FileOperations.readFile(filePath);
    if (!content) {
      return false;
    }

    const newContent = content.replace(searchValue, replaceValue);
    return await FileOperations.writeFile(filePath, newContent);
  }
}


